import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { verifyPayment } from '../services/paymentService';
import api from '../services/api';

const PaymentReturn: React.FC = () => {
  const location = useLocation();
  const [status, setStatus] = useState<'pending' | 'success' | 'sub_failed' | 'error'>('pending');
  const [message, setMessage] = useState('Verifying payment...');
  const [paymentOrderId, setPaymentOrderId] = useState<string | null>(null);
  const [orderType, setOrderType] = useState<'plan' | 'top_up' | null>(null);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const gatewayParam = params.get('gateway') || '';
    const paymentOrderIdParam = params.get('paymentOrderId') || '';

    const stored = localStorage.getItem('paymentReturn');
    const storedData = stored ? JSON.parse(stored) : null;

    const gateway = gatewayParam || storedData?.gateway;
    const orderId = paymentOrderIdParam || storedData?.paymentOrderId;

    if (!gateway || !orderId) {
      setStatus('error');
      setMessage('Missing payment information.');
      return;
    }

    verifyPayment({ gateway, paymentOrderId: orderId })
      .then((data: any) => {
        setPaymentOrderId(data.paymentOrderId || orderId);
        setOrderType(data.orderType || null);
        if (data.subscriptionStatus === 'failed') {
          setStatus('sub_failed');
          setMessage('Payment received but your subscription could not be activated automatically.');
        } else {
          setStatus('success');
          setMessage(
            data.orderType === 'top_up'
              ? `Payment verified — ${data.topUpCredits || 'extra'} unlocks have been added.`
              : 'Payment verified — your subscription is now active.'
          );
        }
      })
      .catch(() => {
        setStatus('error');
        setMessage('Payment verification failed. Please contact support.');
      })
      .finally(() => {
        localStorage.removeItem('paymentReturn');
      });
  }, [location.search]);

  const handleRetry = async () => {
    if (!paymentOrderId) return;
    setRetrying(true);
    try {
      await api.post(`/api/payments/retry-subscription/${paymentOrderId}`);
      setStatus('success');
      setMessage('Payment fulfillment completed successfully.');
    } catch {
      setMessage('Retry failed. Please contact support with your payment reference.');
    } finally {
      setRetrying(false);
    }
  };

  return (
    <Box maxWidth={520} mx="auto" py={{ xs: 4, md: 6 }}>
      <Paper variant="outlined" sx={{ p: 4, borderRadius: 2, boxShadow: '0 20px 50px rgba(7,24,45,0.08)', borderColor: 'rgba(7,24,45,0.08)' }}>
        <Stack spacing={2} alignItems="center" textAlign="center">
          {status === 'pending' && <CircularProgress />}
          <Typography variant="h5" fontWeight={700}>Payment Status</Typography>
          <Typography variant="body2" color="text.secondary">
            {message}
          </Typography>

          {status === 'sub_failed' && (
            <Alert severity="warning" sx={{ width: '100%', textAlign: 'left' }}>
              Your payment was received. Click <strong>Activate subscription</strong> to retry or contact support with your order ID: <code>{paymentOrderId}</code>
            </Alert>
          )}

          <Box display="flex" gap={2} mt={1} flexWrap="wrap" justifyContent="center">
            {status === 'sub_failed' && (
              <Button variant="contained" color="warning" onClick={handleRetry} disabled={retrying}>
                {retrying ? 'Activating…' : 'Activate subscription'}
              </Button>
            )}
            <Button component={RouterLink} to={orderType === 'top_up' ? '/wallet' : '/#pricing'} variant="outlined">
              {orderType === 'top_up' ? 'Back to Wallet' : 'Back to plans'}
            </Button>
            <Button
              component={RouterLink}
              to={orderType === 'top_up' ? '/app/rides' : '/app'}
              variant="contained"
              color="secondary"
            >
              {orderType === 'top_up' ? 'Browse rides' : 'Go to Dashboard'}
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default PaymentReturn;
